import { ChangeEvent, ReactNode, useRef, useState } from 'react';

import { Button, Input, Label, Modal, TypeModalProps, dialogService } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { useAsyncHandler } from '~/hooks/common';

import { InfoText } from '..';

import styles from './ExcelUploadModal.module.scss';

const ExcelUploadModal = ({
  title,
  fileLabel,
  confirmText = '등록',
  confirmDisabled = false,
  infoTexts,
  accept = '.xlsx',
  depth = 1,
  open,
  onCancel,
  onConfirm,
  extraContent,
}: {
  title: TypeModalProps['title'];
  fileLabel?: string;
  confirmText?: TypeModalProps['confirmText'];
  confirmDisabled?: boolean;
  infoTexts: React.ReactNode[];
  accept?: string;
  depth?: TypeModalProps['depth'];
  open: TypeModalProps['open'];
  onCancel: () => void;
  onConfirm: (file: File) => void | Promise<void>;
  extraContent?: ReactNode;
}) => {
  const { runAsync } = useAsyncHandler();

  const [excelFile, setExcelFile] = useState<File | null>(null);

  const fileSearchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchFile = () => {
    fileSearchInputRef?.current?.click();
  };

  const handleSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.persist();

    runAsync(
      async () => {
        const file = e.target.files?.[0];

        if (!file) {
          return;
        }

        setExcelFile(file);
      },
      {
        onError: (error: Error) => {
          dialogService.addDialog({
            children: error?.message,
          });
        },
      }
    );
  };

  const handleConfirm = () => {
    if (!excelFile) {
      return;
    }

    onConfirm(excelFile);
  };

  return (
    <Modal
      title={title}
      confirmText={confirmText}
      size="medium"
      depth={depth}
      disabled={!excelFile || confirmDisabled}
      open={open}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <div className={clsx(styles['modal-content'], fileLabel && styles['labeled'])}>
        <div className={styles['file-section']}>
          {fileLabel && <Label title={fileLabel} />}
          <div className={styles['excel-file-input-container']}>
            <input
              className={styles['excel-file-input']}
              ref={fileSearchInputRef}
              type="file"
              accept={accept}
              multiple={false}
              onChange={handleSelectFile}
            />
            <Input type="text" readOnly size="small" value={excelFile?.name ?? ''} />
            <Button
              className={styles['excel-file-input-button']}
              size="small"
              color="secondary"
              onClick={handleSearchFile}
            >
              파일 찾기
            </Button>
          </div>
          <div className={styles['excel-file-guide']}>
            {infoTexts.map((infoText, index) => (
              <InfoText key={index}>{infoText}</InfoText>
            ))}
          </div>
        </div>
        {extraContent}
      </div>
    </Modal>
  );
};

export default ExcelUploadModal;
